import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CONTACT_INFO } from "../data";
import ContactForm from "../components/ContactForm";

export default function Home() {
  // Cloudflare optimized image URLs
  const heroImg = "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/5961f5ff-ebf1-44e6-b9b5-afb30420abff/slide-1-hd-jpeg_1.webp";
  const whyChooseImg = "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/5961f5ff-ebf1-44e6-b9b5-afb30420abff/image00003.jpeg";
  const corporateImg = "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/5961f5ff-ebf1-44e6-b9b5-afb30420abff/image00001.jpeg";
  const testimonialBg = "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/5961f5ff-ebf1-44e6-b9b5-afb30420abff/hero-epoxy-floor.jpg";
  const craftsmanImg = "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/5961f5ff-ebf1-44e6-b9b5-afb30420abff/craftsman-working.jpg";
  const cardPrivatBg = "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/5961f5ff-ebf1-44e6-b9b5-afb30420abff/epoxy-gallery-2.jpg";
  const cardErhvervBg = "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/5961f5ff-ebf1-44e6-b9b5-afb30420abff/service-industrial.jpg";
  const serviceSeamlessImg = "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/5961f5ff-ebf1-44e6-b9b5-afb30420abff/service-seamless.jpg";
  const serviceEpoxyImg = "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/5961f5ff-ebf1-44e6-b9b5-afb30420abff/service-epoxy.jpg";

  return (
    <>
      <Helmet>
        <title>Halsnæs Epoxygulve | Slidstærke & Fugefrie Gulvløsninger</title>
        <meta
          name="description"
          content="Professionel udlægning af epoxygulve, industrigulve og fugefrie gulve i Halsnæs & Nordsjælland. Få et uforpligtende tilbud fra Simon Babiel i dag!"
        />
        <meta property="og:title" content="Halsnæs Epoxygulve | Slidstærke & Fugefrie Gulvløsninger" />
        <meta
          property="og:description"
          content="Professionel udlægning af epoxygulve, industrigulve og fugefrie gulve i Halsnæs & Nordsjælland. Få et uforpligtende tilbud fra Simon Babiel i dag!"
        />
        <meta property="og:image" content={heroImg} />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      {/* SECTION 1: HERO BANNER (play.divi.express/tile-flooring/ style) */}
      <section className="relative h-[750px] md:h-[850px] flex items-center bg-brand-dark text-white overflow-hidden">
        {/* Background Image with Dark Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src={heroImg}
            alt="Moderne fugefrit gulv"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-12 w-full flex items-center justify-start">
          {/* Left-aligned text wrapper with thick blue left accent border */}
          <div className="border-l-4 border-brand-orange pl-6 md:pl-8 space-y-6 max-w-3xl mt-16">
            <span className="text-xs md:text-sm font-light uppercase tracking-[0.25em] text-white/90 block">
              Specialist i fugefrie gulve
            </span>
            <h1 className="text-4xl md:text-6xl font-extrabold uppercase tracking-tight text-white leading-tight">
              Halsnæs Epoxygulve
            </h1>
            <p className="text-sm md:text-base text-slate-200 leading-relaxed max-w-2xl font-normal">
              Vi leverer slidstærke, fugefrie gulvløsninger i særklasse. Vi garanterer kompromisløs kvalitet, personlig rådgivning og lang holdbarhed, uanset om det gælder industri, erhverv eller private garager.
            </p>
            <div className="pt-2 flex flex-wrap gap-4">
              <a
                href="#kontakt-sektion"
                className="inline-flex items-center justify-center bg-white text-brand-dark px-8 py-3 text-xs font-extrabold uppercase tracking-widest rounded-full hover:bg-brand-orange hover:text-white transition-all duration-300"
              >
                Få et tilbud
              </a>
              <a
                href={`tel:+45${CONTACT_INFO.phone}`}
                className="inline-flex items-center justify-center bg-brand-orange text-white px-8 py-3 text-xs font-extrabold uppercase tracking-widest rounded-full hover:bg-white hover:text-brand-dark transition-all duration-300"
              >
                Ring {CONTACT_INFO.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2: WELCOME SECTION (asymmetric 2-column layout) */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
            {/* Left Column (5 cols) */}
            <div className="md:col-span-5 space-y-2">
              <span className="text-xs md:text-sm font-light uppercase tracking-[0.25em] text-slate-400 block">
                VELKOMMEN
              </span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-brand-orange uppercase tracking-wider leading-tight">
                Til vores side
              </h2>
            </div>
            {/* Right Column (7 cols) */}
            <div className="md:col-span-7">
              <p className="text-sm md:text-base text-slate-500 leading-relaxed font-normal">
                Halsnæs Epoxygulve er din lokale ekspert i slidstærke og fugefrie gulve på hele Sjælland. Vi er en mindre virksomhed uden stor administration, hvilket sikrer dig fornuftige priser, høj kvalitet og direkte kontakt med den samme person gennem hele projektet. Vi tilpasser altid overfladens struktur og egenskaber til dine specifikke behov.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3: FEATURE PROMO CARDS OVERLAY */}
      <section className="relative bg-white z-10 pb-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 -mb-24 relative z-20">
            
            {/* Card 1: Flydemørtel */}
            <div className="relative group overflow-hidden rounded-none shadow-xl h-[380px] flex flex-col justify-end p-8">
              {/* Background Image */}
              <img
                src={craftsmanImg}
                alt="Flydemørtel"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              {/* Blue Wash Overlay */}
              <div className="absolute inset-0 bg-brand-orange/85 mix-blend-multiply transition-opacity duration-300"></div>
              <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/90 via-brand-orange/40 to-transparent"></div>

              {/* Content */}
              <div className="relative z-10 text-left space-y-3">
                <span className="text-xs font-light uppercase tracking-[0.25em] text-white/90 block">
                  FORBEREDELSE
                </span>
                <h3 className="text-2xl font-extrabold text-white uppercase tracking-wider">
                  Flydemørtel
                </h3>
                <p className="text-xs text-slate-100 leading-relaxed">
                  Cementbaseret afretning, der sikrer et fuldstændig plant, jævnt og stærkt undergulv.
                </p>
                <div className="pt-2">
                  <Link
                    to="/flydemoertel"
                    className="inline-flex items-center justify-center bg-white text-brand-dark px-6 py-2 text-[10px] font-extrabold uppercase tracking-widest rounded-full hover:bg-brand-dark hover:text-white transition-all duration-300"
                  >
                    Læs mere
                  </Link>
                </div>
              </div>
            </div>

            {/* Card 2: Kvartsgulve */}
            <div className="relative group overflow-hidden rounded-none shadow-xl h-[380px] flex flex-col justify-end p-8">
              {/* Background Image */}
              <img
                src={serviceSeamlessImg}
                alt="Kvartsgulve"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              {/* Blue Wash Overlay */}
              <div className="absolute inset-0 bg-brand-orange/85 mix-blend-multiply transition-opacity duration-300"></div>
              <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/90 via-brand-orange/40 to-transparent"></div>

              {/* Content */}
              <div className="relative z-10 text-left space-y-3">
                <span className="text-xs font-light uppercase tracking-[0.25em] text-white/90 block">
                  INDUSTRI & ERHVERV
                </span>
                <h3 className="text-2xl font-extrabold text-white uppercase tracking-wider">
                  Kvartsgulve
                </h3>
                <p className="text-xs text-slate-100 leading-relaxed">
                  Ekstremt robuste og skridsikre gulve opbygget af farvet kvartssand og epoxy.
                </p>
                <div className="pt-2">
                  <Link
                    to="/kvartsgulve"
                    className="inline-flex items-center justify-center bg-white text-brand-dark px-6 py-2 text-[10px] font-extrabold uppercase tracking-widest rounded-full hover:bg-brand-dark hover:text-white transition-all duration-300"
                  >
                    Læs mere
                  </Link>
                </div>
              </div>
            </div>

            {/* Card 3: Epoxygulve */}
            <div className="relative group overflow-hidden rounded-none shadow-xl h-[380px] flex flex-col justify-end p-8">
              {/* Background Image */}
              <img
                src={serviceEpoxyImg}
                alt="Epoxygulve"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              {/* Blue Wash Overlay */}
              <div className="absolute inset-0 bg-brand-orange/85 mix-blend-multiply transition-opacity duration-300"></div>
              <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/90 via-brand-orange/40 to-transparent"></div>

              {/* Content */}
              <div className="relative z-10 text-left space-y-3">
                <span className="text-xs font-light uppercase tracking-[0.25em] text-white/90 block">
                  SLIDSTÆRK FINISH
                </span>
                <h3 className="text-2xl font-extrabold text-white uppercase tracking-wider">
                  Epoxygulve
                </h3>
                <p className="text-xs text-slate-100 leading-relaxed">
                  Klassiske fugefrie og rengøringsvenlige gulve med en uovertruffen slidstyrke.
                </p>
                <div className="pt-2">
                  <Link
                    to="/epoxygulve"
                    className="inline-flex items-center justify-center bg-white text-brand-dark px-6 py-2 text-[10px] font-extrabold uppercase tracking-widest rounded-full hover:bg-brand-dark hover:text-white transition-all duration-300"
                  >
                    Læs mere
                  </Link>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>



      {/* SECTION 5: MIDDLE CONTENT SPACER (Services, Why Choose Us, Simon Babiel, Testimonials, Contact Form) */}
      <div className="bg-white w-full">
        
        {/* 5.2: Why Choose Us Section */}
        <section className="py-24 bg-slate-50">
          <div className="mx-auto max-w-7xl px-6 md:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              
              {/* Left Column: Image with Overlapping Widget */}
              <div className="relative">
                <img
                  src={whyChooseImg}
                  alt="Epoxygulv installation"
                  className="w-full h-[450px] object-cover rounded-none shadow-xl"
                />
                {/* Floating Interactive Widget */}
                <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-none shadow-2xl border border-slate-100 hidden sm:block max-w-[280px]">
                  <h4 className="text-xs font-extrabold text-brand-dark uppercase tracking-wider mb-3 pb-2 border-b border-slate-100">
                    Vores kernefokus
                  </h4>
                  <ul className="space-y-2 text-xs text-slate-600 font-semibold">
                    <li className="flex items-center gap-2">
                      <i className="fa-solid fa-circle-check text-brand-orange"></i>
                      <span>Høj slidstyrke</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <i className="fa-solid fa-circle-check text-brand-orange"></i>
                      <span>Fugefri rengøring</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <i className="fa-solid fa-circle-check text-brand-orange"></i>
                      <span>Kemikalieresistent</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Right Column: Text & Stats */}
              <div className="space-y-6 text-left">
                <span className="text-xs md:text-sm font-light uppercase tracking-[0.25em] text-slate-400 block">
                  MINDRE VIRKSOMHED, HØJ KVALITET
                </span>
                <h2 className="text-3xl md:text-4xl font-extrabold text-brand-dark leading-snug uppercase tracking-wider">
                  Hvorfor vælge os?
                </h2>
                <p className="text-xs md:text-sm text-slate-600 leading-relaxed">
                  Halsnæs Epoxygulve er en mindre virksomhed uden stor administration og skiftende medarbejdere. Det betyder, at du som kunde hos os altid kan forvente fornuftige priser, høj og ensartet kvalitet samt en god, direkte dialog med den samme kontaktperson gennem hele forløbet. Vi lægger vægt på stor fleksibilitet og overholdelse af alle aftaler.
                </p>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-6 pt-4">
                  <div className="bg-white p-6 rounded-none border border-slate-200 shadow-xs flex items-center gap-4">
                    <div className="text-brand-orange text-3xl">
                      <i className="fa-solid fa-award"></i>
                    </div>
                    <div>
                      <h4 className="text-2xl font-extrabold text-brand-dark">100%</h4>
                      <p className="text-[10px] text-slate-500 uppercase tracking-wider font-bold">Kvalitetsgaranti</p>
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-none border border-slate-200 shadow-xs flex items-center gap-4">
                    <div className="text-brand-orange text-3xl">
                      <i className="fa-solid fa-handshake"></i>
                    </div>
                    <div>
                      <h4 className="text-2xl font-extrabold text-brand-dark">Direkte</h4>
                      <p className="text-[10px] text-slate-500 uppercase tracking-wider font-bold">Ejer-kontakt</p>
                    </div>
                  </div>
                </div>

                <div className="pt-4">
                  <a
                    href="#kontakt-sektion"
                    className="inline-flex items-center justify-center bg-brand-orange text-white px-8 py-3.5 text-xs font-extrabold uppercase tracking-widest rounded-full hover:bg-brand-dark transition-all duration-300"
                  >
                    Få et tilbud
                  </a>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* 5.3: Solid Deep Blue Visual Break Block */}
        <section className="bg-brand-teal py-20 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute -left-10 -top-10 w-40 h-40 rounded-full bg-brand-orange blur-3xl"></div>
            <div className="absolute -right-10 -bottom-10 w-40 h-40 rounded-full bg-brand-orange blur-3xl"></div>
          </div>
          <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-12 text-center">
            <h3 className="text-xl md:text-2xl font-extrabold uppercase tracking-wider text-white max-w-3xl mx-auto leading-snug">
              Vi dækker hele Sjælland – herunder København, Hillerød, Roskilde, Næstved, Køge, Helsingør og Slagelse.
            </h3>
          </div>
        </section>

        {/* 5.4: Corporate Clients Section */}
        <section className="py-24 bg-white">
          <div className="mx-auto max-w-4xl px-6 md:px-12">
            
              {/* Right Column: Content Grid */}
              <div className="space-y-6 text-left">
                <span className="text-xs md:text-sm font-light uppercase tracking-[0.25em] text-slate-400 block">
                  ALSIDIG KUNDEKREDS
                </span>
                <h2 className="text-3xl md:text-4xl font-extrabold text-brand-dark leading-snug uppercase tracking-wider">
                  Gulve tilpasset dit behov
                </h2>
                <p className="text-xs md:text-sm text-slate-600 leading-relaxed">
                  Vores kundekreds er alsidig og omfatter en bred vifte af virksomheder inden for industri, produktion og detail, samt private boligejere. Vi tilpasser altid overfladens struktur og egenskaber til det konkrete formål.
                </p>

                {/* Mini Service Blocks Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-4">
                  
                  <div className="flex gap-3">
                    <span className="text-brand-orange text-xl shrink-0 mt-1">
                      <i className="fa-solid fa-industry"></i>
                    </span>
                    <div>
                      <h4 className="text-xs font-bold text-brand-dark uppercase tracking-wider">Industri & Erhverv</h4>
                      <p className="text-[11px] text-slate-500 mt-1">Lagerhaller, produktionsmiljøer, detailbutikker og kontorer.</p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <span className="text-brand-orange text-xl shrink-0 mt-1">
                      <i className="fa-solid fa-car"></i>
                    </span>
                    <div>
                      <h4 className="text-xs font-bold text-brand-dark uppercase tracking-wider">Garager & Værksteder</h4>
                      <p className="text-[11px] text-slate-500 mt-1">Ekstremt slidstærke overflader, der tåler olie og tung belastning.</p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <span className="text-brand-orange text-xl shrink-0 mt-1">
                      <i className="fa-solid fa-house"></i>
                    </span>
                    <div>
                      <h4 className="text-xs font-bold text-brand-dark uppercase tracking-wider">Private Boliger</h4>
                      <p className="text-[11px] text-slate-500 mt-1">Kældergulve, bryggers, badeværelser og vaskehuse.</p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <span className="text-brand-orange text-xl shrink-0 mt-1">
                      <i className="fa-solid fa-shield-halved"></i>
                    </span>
                    <div>
                      <h4 className="text-xs font-bold text-brand-dark uppercase tracking-wider">Høje krav</h4>
                      <p className="text-[11px] text-slate-500 mt-1">Skridsikre og kemikalieresistente løsninger til alle brancher.</p>
                    </div>
                  </div>

                </div>
              </div>
          </div>
        </section>

        {/* 5.5: Executive Team Section */}
        <section className="py-24 bg-slate-50 border-t border-slate-200">
          <div className="mx-auto max-w-7xl px-6 md:px-12 text-center">
            <span className="text-xs md:text-sm font-light uppercase tracking-[0.25em] text-slate-400 block mb-2">
              DIN GARANTI FOR KVALITET
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-brand-dark leading-snug mb-12 uppercase tracking-wider">
              Mød Simon Babiel
            </h2>

            <div className="max-w-3xl mx-auto space-y-6 text-slate-600 leading-relaxed mb-16">
              <p className="text-base md:text-lg font-medium text-brand-dark">
                "Som ejer af Halsnæs Epoxygulve er jeg personligt involveret i alle projekter fra den indledende rådgivning til den afsluttende kvalitetssikring."
              </p>
              <p className="text-xs md:text-sm">
                Når du samarbejder med os, slipper du for skiftende medarbejdere og stor administration. Du har den samme kontaktperson gennem hele forløbet, hvilket sikrer en god dialog, stor fleksibilitet og et resultat, der lever fuldstændig op til dine forventninger.
              </p>
            </div>

            {/* Bottom Footer Stack */}
            <div className="border-t border-slate-200 pt-8 grid grid-cols-1 md:grid-cols-2 gap-8 text-left max-w-4xl mx-auto">
              <div className="pr-0 md:pr-8 border-b md:border-b-0 md:border-r border-slate-200 pb-6 md:pb-0">
                <p className="text-xs md:text-sm text-slate-500">
                  Vi anvender udelukkende de nyeste teknikker og godkendte materialer af højeste kvalitet. Det sikrer dig et gulv, der holder i mange år fremover.
                </p>
              </div>
              <div className="pl-0 md:pl-8 flex flex-col justify-center">
                <h4 className="text-xs font-extrabold text-brand-dark uppercase tracking-wider mb-1">Har du spørgsmål?</h4>
                <a
                  href={`tel:+45${CONTACT_INFO.phone}`}
                  className="text-2xl font-extrabold text-brand-orange hover:text-brand-dark transition-colors duration-300"
                >
                  +45 {CONTACT_INFO.phone}
                </a>
              </div>
            </div>

          </div>
        </section>

        {/* 5.6: Testimonials Section */}
        <section className="relative py-24 text-white overflow-hidden">
          {/* Background Image with Dark Overlay */}
          <div className="absolute inset-0 z-0">
            <img
              src={testimonialBg}
              alt="Epoxygulv i værksted"
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-zinc-950/90"></div>
          </div>

          <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              
              {/* Left Side (Clear space for visual balance) */}
              <div className="lg:col-span-5 hidden lg:block">
                <div className="border-l-4 border-brand-orange pl-6">
                  <h3 className="text-2xl font-extrabold uppercase tracking-wider text-white">Over 8 års erfaring med fugefrie gulve</h3>
                  <p className="text-xs text-slate-400 mt-2">Vi leverer altid topkvalitet og professionel service til aftalt tid.</p>
                </div>
              </div>

              {/* Right Side: Testimony Details */}
              <div className="lg:col-span-7 bg-brand-teal/80 p-8 md:p-12 rounded-none border border-slate-700/50 backdrop-blur-xs text-left space-y-6">
                <span className="text-xs md:text-sm font-light uppercase tracking-[0.25em] text-white/90 block">
                  KUNDEUDTALELSE
                </span>
                <h3 className="text-2xl md:text-3xl font-extrabold uppercase tracking-wider text-white leading-snug">
                  Hvad folk siger om os
                </h3>
                <blockquote className="text-sm md:text-base italic text-slate-100 leading-relaxed">
                  "Halsnæs Epoxygulve har lagt et fantastisk flot og slidstærkt epoxygulv i vores værksted. Det tåler spild af olie og tunge donkrafte uden problemer. Simon leverede til tiden og ydede en super service."
                </blockquote>
                <div>
                  <h4 className="text-xs font-extrabold text-white uppercase tracking-wider">Mads Hansen</h4>
                  <p className="text-[11px] text-slate-300">Værkfører, MH Autoteknik</p>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* 5.7: Newsletter & Social Footer Bar */}
        <section className="bg-brand-orange text-white py-8" id="kontakt-sektion">
          <div className="mx-auto max-w-7xl px-6 md:px-12">
            <div className="flex flex-col lg:flex-row justify-between items-center gap-6">
              <div>
                <h3 className="text-lg md:text-xl font-extrabold uppercase tracking-wider">Modtag et gratis og uforpligtende tilbud</h3>
                <p className="text-xs text-slate-100 mt-1">Udfyld dine oplysninger herunder, så vender vi tilbage til dig hurtigst muligt.</p>
              </div>
              <div className="flex flex-wrap gap-4 items-center">
                <a
                  href={`tel:+45${CONTACT_INFO.phone}`}
                  className="inline-flex items-center justify-center bg-brand-dark text-white px-6 py-3 text-xs font-extrabold uppercase tracking-widest rounded-full hover:bg-white hover:text-brand-dark transition-all duration-300"
                >
                  Ring mig op
                </a>
                <a
                  href={`mailto:${CONTACT_INFO.email}`}
                  className="inline-flex items-center justify-center bg-white text-brand-dark px-6 py-3 text-xs font-extrabold uppercase tracking-widest rounded-full hover:bg-brand-dark hover:text-white transition-all duration-300"
                >
                  Send e-mail
                </a>
              </div>
            </div>
          </div>
        </section>

      </div>

      {/* FLOATING CTA BUTTONS */}
      <div className="fixed right-6 bottom-6 z-50 flex flex-col gap-3">
        {/* Phone Button */}
        <a
          href={`tel:+45${CONTACT_INFO.phone}`}
          className="w-12 h-12 rounded-full bg-brand-orange text-white flex items-center justify-center shadow-lg hover:bg-brand-teal hover:scale-110 transition-all duration-300"
          aria-label="Ring til os"
        >
          <i className="fa-solid fa-phone text-lg"></i>
        </a>
        {/* Email Button */}
        <a
          href={`mailto:${CONTACT_INFO.email}`}
          className="w-12 h-12 rounded-full bg-brand-orange text-white flex items-center justify-center shadow-lg hover:bg-brand-teal hover:scale-110 transition-all duration-300"
          aria-label="Send e-mail"
        >
          <i className="fa-solid fa-envelope text-lg"></i>
        </a>
        {/* Instagram Button (Social) */}
        <a
          href="https://www.instagram.com/halsnaes_epoxygulve?igsh=eDkwbG9yaHNpdDN0&utm_source=qr"
          target="_blank"
          rel="noopener noreferrer"
          className="w-12 h-12 rounded-full bg-brand-orange text-white flex items-center justify-center shadow-lg hover:bg-brand-teal hover:scale-110 transition-all duration-300"
          aria-label="Følg os på Instagram"
        >
          <i className="fa-brands fa-instagram text-lg"></i>
        </a>
      </div>
    </>
  );
}
